import type { HTMLAttributes, ReactNode, Ref } from "react";

import { cn } from "../../lib/utils";
import { AnalyticsCardToolbar } from "./AnalyticsCardToolbar";
import { AnalyticsCardValue } from "./AnalyticsCardValue";
import { AnalyticsCardChart } from "./AnalyticsCardChart";

/**
 * A molecule (not an atom): its toolbar part composes the Button atom and DropdownMenu, which is
 * what earns it molecule status.
 *
 * A query/analytics card shell -- feature-agnostic and data-shape-agnostic by design: `title`,
 * `description` and `meta` carry whatever the consumer is aggregating (patient counts, calendar
 * events booked, ticket volume, revenue, …). This card has no opinion on what the center section
 * shows: it's just `children`, centered in the remaining space below the header. Two ready-made
 * sub-components cover the common cases -- `AnalyticsCard.Value` (a large centered Button showing
 * one number) and `AnalyticsCard.Chart` (a line graph over a `{label, value}[]` series) -- but
 * nothing requires using either; any single node works.
 *
 * The toolbar is composed, not configured: pass `<AnalyticsCard.Toolbar />` as a child to get one,
 * omit it for a read-only placement. It is absolutely positioned against this card's `relative`
 * root, so its position among the children doesn't affect the centered content box. Everything it
 * takes (actions, menu items, drag handle props, dragging) belongs to the toolbar itself rather
 * than being mirrored through this card -- including the size-toggle affordance a board wants,
 * which is an ordinary `AnalyticsCardToolbarAction` the parent composes with its own
 * icon/label/onClick. It depends on this card's `group/card` root, so it is reachable only as
 * `AnalyticsCard.Toolbar`, never as a standalone export.
 *
 * This card holds no layout opinion of its own: it is `h-full w-full`, filling whatever box it's
 * placed in. Both of the layout facts a board cares about -- how many columns a card spans and how
 * tall a row is -- belong to the parent grid (e.g. `SortableList`, via its Item's `span` and the
 * consumer's row-height className); a card sized in grid terms ("half", "full") would only be
 * duplicating that. The consequence to know: a standalone placement outside a grid must give the
 * card a height, since `h-full` in an auto-height parent collapses to content height.
 *
 * @example
 * ```tsx
 * <AnalyticsCard title="New patients this week" meta="Updated 5 minutes ago">
 *   <AnalyticsCard.Value onClick={...}>76</AnalyticsCard.Value>
 * </AnalyticsCard>
 *
 * <AnalyticsCard title="Patient visits">
 *   <AnalyticsCard.Chart data={series} />
 * </AnalyticsCard>
 *
 * <AnalyticsCard title="New patients this week">
 *   <AnalyticsCard.Value>76</AnalyticsCard.Value>
 *   <AnalyticsCard.Toolbar actions={actions} dragHandleProps={dragHandleProps} dragging={dragging} />
 * </AnalyticsCard>
 * ```
 */
export interface AnalyticsCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  ref?: Ref<HTMLDivElement>;
  title: ReactNode;
  description?: ReactNode;
  /**
   * Trailing header slot, opposite the title -- a formatted "Updated 5 minutes ago", a Badge, a
   * record count, a status dot. A plain node with no semantics of its own. This card deliberately
   * does no date formatting: a consumer that wants relative time formats it and passes the
   * resulting node.
   */
  meta?: ReactNode;
}

const AnalyticsCardRoot = ({ className, title, description, meta, children, ref, ...props }: AnalyticsCardProps) => {
  return (
    <div
      ref={ref}
      data-slot="analytics-card"
      className={cn(
        "group/card relative flex h-full w-full flex-col rounded-[calc(var(--radius)-2px)] border border-border bg-card p-4 text-card-foreground",
        className
      )}
      {...props}
    >
      <div data-slot="analytics-card-header" className="flex flex-col gap-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="min-w-0 truncate text-sm font-medium leading-snug">{title}</span>
          {meta ? <span className="shrink-0 text-xs leading-snug text-muted-foreground">{meta}</span> : null}
        </div>
        {description ? <span className="text-xs leading-snug text-muted-foreground">{description}</span> : null}
      </div>
      <div data-slot="analytics-card-value" className="flex min-h-0 flex-1 items-center justify-center pt-0">
        {children}
      </div>
    </div>
  );
};

export const AnalyticsCard = Object.assign(AnalyticsCardRoot, {
  Value: AnalyticsCardValue,
  Chart: AnalyticsCardChart,
  Toolbar: AnalyticsCardToolbar,
});
